import { useEffect, useLayoutEffect, useRef } from 'react';

import type { AdElement, ElementTool, NewElementSpec } from '@adflow/types';

import type { EditorCanvasHistory } from '../store/history';
import type { CanvasViewport } from './useCanvasViewport';

const TOOL_SHORTCUTS: Record<string, ElementTool> = {
  t: 'TEXT',
  r: 'RECTANGLE',
  o: 'ELLIPSE',
};

/** Ignore keyboard shortcuts when we are on an editable element. E.g: typing in a text input. */
export function shouldIgnoreCanvasShortcut(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const contentEditable = target.getAttribute('contenteditable');

  if (target.isContentEditable || contentEditable === '' || contentEditable === 'true') {
    return true;
  }

  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

function isSpace(event: KeyboardEvent) {
  return event.key === ' ' || event.code === 'Space';
}

export type UseCanvasShortcutsArgs = {
  camera: Pick<
    CanvasViewport,
    'canArmPan' | 'setPanArmed' | 'zoomIn' | 'zoomOut' | 'resetViewport'
  >;
  cancelGesture: () => void;
  elements: AdElement[];
  selectedElementIds: string[];
  history?: EditorCanvasHistory;
  activeTool: ElementTool | null;
  onActiveToolChange?: (tool: ElementTool | null) => void;
  onInsertElement?: (spec: NewElementSpec) => void;
  onDeleteElements?: (elementIds: string[]) => void;
};

export function useCanvasShortcuts(args: UseCanvasShortcutsArgs): void {
  const argsRef = useRef(args);
  useLayoutEffect(() => {
    argsRef.current = args;
  });

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const {
        camera,
        cancelGesture,
        elements,
        selectedElementIds,
        history,
        activeTool,
        onActiveToolChange,
        onInsertElement,
        onDeleteElements,
      } = argsRef.current;

      if (event.key === 'Escape') {
        // Esc drops the gesture in progress and puts the tool away.
        cancelGesture();
        if (activeTool) {
          onActiveToolChange?.(null);
        }
        return;
      }

      if (shouldIgnoreCanvasShortcut(event.target)) {
        return;
      }

      const hasModifier = event.metaKey || event.ctrlKey || event.altKey;

      const shortcutTool = hasModifier ? undefined : TOOL_SHORTCUTS[event.key.toLowerCase()];
      if (shortcutTool && onActiveToolChange && onInsertElement) {
        event.preventDefault();
        onActiveToolChange(shortcutTool);
        return;
      }

      if (
        (event.key === 'Backspace' || event.key === 'Delete') &&
        !hasModifier &&
        onDeleteElements &&
        selectedElementIds.length > 0
      ) {
        // The frame itself can be selected from the Elements list, but never deleted by key.
        const deletable = selectedElementIds.filter((id) =>
          elements.some((element) => element.id === id && element.type !== 'SVG'),
        );
        if (deletable.length > 0) {
          event.preventDefault();
          onDeleteElements(deletable);
        }
        return;
      }

      if (isSpace(event)) {
        if (event.target instanceof HTMLElement && event.target.closest('button')) {
          return;
        }
        if (!camera.canArmPan(event.target)) {
          return;
        }
        event.preventDefault();
        camera.setPanArmed(true);
        return;
      }

      const meta = event.metaKey || event.ctrlKey;
      if (meta && (event.key === '+' || event.key === '=' || event.code === 'Equal')) {
        event.preventDefault();
        camera.zoomIn();
        return;
      }
      if (meta && (event.key === '-' || event.key === '_' || event.code === 'Minus')) {
        event.preventDefault();
        camera.zoomOut();
        return;
      }
      if (meta && (event.key === '0' || event.code === 'Digit0')) {
        event.preventDefault();
        camera.resetViewport();
        return;
      }

      if (meta && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) {
          history?.redo();
          return;
        }
        history?.undo();
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (isSpace(event)) {
        argsRef.current.camera.setPanArmed(false);
      }
    };

    const onBlur = () => {
      argsRef.current.camera.setPanArmed(false);
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);
}
